import React, { useEffect, useState, useMemo } from "react";
import { collection, getDocs, addDoc, serverTimestamp, query, where, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import BlankLayout from "../components/BlankLayout";
import { useNavigate } from "react-router-dom";

function CdmBilling() {
  const navigate = useNavigate();

  const [userProfile, setUserProfile] = useState(null);
  const [hawkerList, setHawkerList] = useState([]);
  const [selectedHawker, setSelectedHawker] = useState(null);
  const [cdmProducts, setCdmProducts] = useState([]);
  const [items, setItems] = useState({}); // MSKU -> quantity
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeDivision, setActiveDivision] = useState("ALL");
  const [search, setSearch] = useState("");

  // AUTH
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/");
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (!snap.exists()) {
        alert("User profile missing");
        setLoading(false);
        return;
      }
      setUserProfile(snap.data());
    });
    return () => unsub();
  }, [navigate]);

  // LOAD HAWKERS & PRODUCTS
  useEffect(() => {
    if (!userProfile) return;
    const loadData = async () => {
      try {
        // 1. Fetch only hawkers
        const hawkerSnap = await getDocs(
          query(
            collection(db, "partyMaster"),
            where("clientId", "==", userProfile.clientId),
            where("partytype", "==", "hawker")
          )
        );
        const hawkers = hawkerSnap.docs.map(d => d.data());
        setHawkerList(hawkers);

        // 2. Fetch CDM products
        const cdmSnap = await getDocs(
          query(
            collection(db, "cdmMaster"),
            where("clientId", "==", userProfile.clientId)
          )
        );
        const prods = cdmSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        prods.sort((a, b) => (Number(a.rate) || 0) - (Number(b.rate) || 0));
        setCdmProducts(prods);

        // Initialize quantities to empty string
        const list = {};
        prods.forEach(p => {
          if (p.id) list[p.id] = "";
        });
        setItems(list);
      } catch (err) {
        console.error("Error loading CDM billing data:", err);
      }
      setLoading(false);
    };
    loadData();
  }, [userProfile]);

  // DYNAMIC DIVISION LIST
  const divisionsList = useMemo(() => {
    const set = new Set();
    cdmProducts.forEach(p => {
      if (p.division) set.add(p.division.toUpperCase());
    });
    const sortedDivs = Array.from(set).sort((a, b) => a.localeCompare(b));
    return ["ALL", ...sortedDivs];
  }, [cdmProducts]);

  // TOTALS
  const totals = useMemo(() => {
    let totalQty = 0;
    let totalAmount = 0;
    cdmProducts.forEach(p => {
      const key = p.id;
      const qty = Number(items[key] || 0);
      if (qty > 0) {
        totalQty += qty;
        totalAmount += qty * (Number(p.rate) || 0);
      }
    });
    return { totalQty, totalAmount };
  }, [items, cdmProducts]);

  // QUANTITY HANDLERS
  const adjustQty = (productId, delta) => {
    if (!selectedHawker) {
      alert("⚠️ Please select Hawker Name first");
      return;
    }
    const key = productId;
    const current = Number(items[key] || 0);
    const next = Math.max(0, current + delta);
    setItems({ ...items, [key]: next === 0 ? "" : String(next) });
    try {
      if (navigator.vibrate) navigator.vibrate(50);
    } catch (e) {
      console.warn("Haptic feedback failed:", e);
    }
  };

  const submitBilling = async () => {
    if (!selectedHawker) {
      alert("⚠️ Please select Hawker Name");
      return;
    }
    if (totals.totalQty <= 0) {
      alert("⚠️ Please enter a quantity for at least one product.");
      return;
    }
    
    try {
      setSubmitting(true);
      const cleanItems = {};
      cdmProducts.forEach(p => {
        const key = p.id;
        const qty = Number(items[key] || 0);
        if (qty > 0) {
          cleanItems[key] = {
            productName: p.productName,
            msku: p.msku || "",
            division: p.division,
            category: p.category,
            rate: p.rate,
            qty
          };
        }
      });

      await addDoc(collection(db, "cdmOrders"), {
        clientId: userProfile.clientId,
        partyId: selectedHawker.partyId,
        partyName: selectedHawker.name || selectedHawker.partyId,
        items: cleanItems,
        totalQty: totals.totalQty,
        totalAmount: totals.totalAmount,
        status: "PENDING",
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "salesman"
      });

      setSubmitted(true);
      try {
        if (navigator.vibrate) navigator.vibrate(200);
      } catch (e) {
        console.warn("Haptic feedback failed:", e);
      }
    } catch (err) {
      console.error("Submission failed:", err);
      alert("Billing submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // FILTERED PRODUCTS
  const filteredProducts = useMemo(() => {
    return cdmProducts.filter(p => {
      const productName = p.productName || "";
      const msku = p.msku || "";
      const division = p.division || "";
      const matchesSearch = productName.toLowerCase().includes(search.toLowerCase()) || 
                            msku.toLowerCase().includes(search.toLowerCase());
      const matchesDivision = activeDivision === "ALL" || 
                              division.toUpperCase() === activeDivision.toUpperCase();
      return matchesSearch && matchesDivision;
    });
  }, [cdmProducts, activeDivision, search]);

  if (loading) return <BlankLayout><p style={{ padding: 20 }}>Loading CDM Billing...</p></BlankLayout>;

  if (submitted) {
    return (
      <BlankLayout>
        <div style={{ padding: 40, textAlign: "center", fontFamily: "'Inter', sans-serif" }}>
          <h2 style={{ color: "#2e7d32", fontWeight: "900" }}>✅ Bill Submitted</h2>
          <p style={{ fontSize: 18, marginTop: 10 }}>CDM Billing transaction created successfully.</p>
          <button
            onClick={() => navigate("/base")}
            style={{ marginTop: 25, padding: "14px 24px", fontSize: 18, background: "#673ab7", color: "#fff", border: "none", borderRadius: 12, fontWeight: "bold", cursor: "pointer" }}
          >
            Go to Dashboard
          </button>
        </div>
      </BlankLayout>
    );
  }

  return (
    <BlankLayout>
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: "#f8f9fa", fontFamily: "'Inter', sans-serif" }}>
        {/* STICKY HEADER */}
        <div style={{ padding: "16px", background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", zIndex: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <button onClick={() => navigate("/base")} style={{ padding: "8px 12px", background: "#f0f0f0", border: "1px solid #ccc", borderRadius: 8, cursor: "pointer" }}>← HOME</button>
            <span style={{ fontWeight: "800", color: "#673ab7", fontSize: "16px" }}>CDM Billing</span>
          </div>

          <select
            value={selectedHawker?.partyId || ""}
            onChange={e => setSelectedHawker(hawkerList.find(p => p.partyId === e.target.value))}
            style={{ width: "100%", padding: "14px", fontSize: "18px", fontWeight: "700", borderRadius: "10px", border: `2px solid ${!selectedHawker ? "#ffc107" : "#ddd"}`, background: !selectedHawker ? "#fff9e6" : "#fff", marginBottom: 12 }}
          >
            <option value="">-- SELECT HAWKER --</option>
            {hawkerList.map(p => <option key={p.partyId} value={p.partyId}>{p.name ? `${p.name} (${p.partyId})` : p.partyId}</option>)}
          </select>

          <input
            type="text" 
            placeholder="🔍 Search CDM Product..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", height: 48, padding: "0 14px", fontSize: 16, borderRadius: 10, border: "1px solid #bbb", marginBottom: 12, boxSizing: "border-box" }}
          />

          {/* DYNAMIC DIVISION TABS */}
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
            {divisionsList.map(div => (
              <button
                key={div}
                onClick={() => setActiveDivision(div)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "20px",
                  border: "none",
                  fontSize: "13px",
                  fontWeight: "800",
                  cursor: "pointer",
                  background: activeDivision === div ? "#673ab7" : "#f1f3f5",
                  color: activeDivision === div ? "#fff" : "#495057",
                  transition: "all 0.2s"
                }}
              >
                {div}
              </button>
            ))}
          </div>
        </div>

        {/* SCROLLABLE PRODUCT LIST */}
        <div style={{ flex: 1, position: "relative", overflowY: "auto", padding: "16px 16px 140px 16px" }}>
          {!selectedHawker && (
            <div style={{ 
              position: "absolute", 
              top: 0, left: 0, right: 0, bottom: 0, 
              background: "rgba(255,255,255,0.7)", 
              backdropFilter: "blur(6px)",
              zIndex: 50,
              display: "flex", 
              flexDirection: "column", 
              alignItems: "center", 
              paddingTop: "60px",
              textAlign: "center"
            }}>
              <div style={{ 
                fontSize: "60px", 
                marginBottom: "20px", 
                color: "#ff9800"
              }}>
                ☝️
              </div>
              <h2 style={{ fontSize: "24px", fontWeight: "900", color: "#1f2937", margin: "0 20px" }}>
                CHOOSE HAWKER
              </h2>
              <p style={{ color: "#4b5563", fontSize: "16px", marginTop: "10px", fontWeight: "600" }}>
                to enable products entry
              </p>
            </div>
          )}

          {filteredProducts.map((p) => {
            const key = p.id;
            const hasQty = Number(items[key] || 0) > 0;
            return (
              <div key={p.id || p.msku} style={{ marginBottom: 12, padding: 14, border: hasQty ? "2px solid #2e7d32" : "1px solid #ddd", borderRadius: 14, background: hasQty ? "#f4f9f4" : "#fff", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: "700", color: "#2d3748" }}>{p.productName}</div>
                    <div style={{ fontSize: 12, color: "#a0aec0", marginTop: 2 }}>{p.msku} | Division: {p.division}</div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: "800", color: "#2e7d32" }}>₹{Number(p.rate).toFixed(2)}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <button 
                    disabled={!selectedHawker} 
                    onClick={() => adjustQty(p.id, -1)} 
                    style={{ 
                      flexShrink: 0,
                      width: 50, 
                      height: 56, 
                      borderRadius: 10, 
                      background: "#fff", 
                      border: "2px solid #ffc9c9", 
                      color: "#fa5252", 
                      fontSize: 28, 
                      fontWeight: "900",
                      cursor: "pointer"
                    }}
                  >
                    −
                  </button>
                  <input
                    type="number" 
                    inputMode="numeric"
                    pattern="\d*"
                    value={items[key] || ""} 
                    disabled={!selectedHawker} 
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setItems({ ...items, [key]: val });
                    }}
                    placeholder="0"
                    style={{ 
                      flex: 1, 
                      minWidth: 0,
                      height: 56, 
                      textAlign: "center", 
                      fontSize: 24, 
                      fontWeight: "900", 
                      borderRadius: 10, 
                      border: "2px solid #ddd", 
                      background: !selectedHawker ? "#f8f9fa" : "#fff", 
                      color: "#000",
                      WebkitAppearance: "none",
                      margin: 0
                    }}
                  />
                  <button 
                    disabled={!selectedHawker} 
                    onClick={() => adjustQty(p.id, 1)} 
                    style={{ 
                      flexShrink: 0,
                      width: 50, 
                      height: 56, 
                      borderRadius: 10, 
                      background: "#fff", 
                      border: "2px solid #b2f2bb", 
                      color: "#40c057", 
                      fontSize: 28, 
                      fontWeight: "900",
                      cursor: "pointer"
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
          {filteredProducts.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#a0aec0" }}>No CDM products found matching the filters.</div>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ 
          position: "fixed", 
          bottom: 0, 
          left: 0,
          right: 0,
          background: "#fff", 
          borderTop: "2px solid #eee", 
          padding: "12px 16px env(safe-area-inset-bottom) 16px", 
          boxShadow: "0 -4px 12px rgba(0,0,0,0.1)",
          boxSizing: "border-box",
          zIndex: 100
        }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12, fontSize: "18px", fontWeight: "700" }}>
            <span>Total Quantity: <span style={{ fontSize: "24px", color: "#673ab7" }}>{totals.totalQty}</span></span>
          </div>
          <button 
            onClick={() => setShowPreview(true)} 
            disabled={submitting || totals.totalQty <= 0}
            style={{ 
              width: "100%", 
              padding: 18, 
              background: submitting || totals.totalQty <= 0 ? "#adb5bd" : "#673ab7", 
              color: "#fff", 
              border: "none", 
              borderRadius: 14, 
              fontSize: 24, 
              fontWeight: "900",
              cursor: submitting || totals.totalQty <= 0 ? "default" : "pointer"
            }}
          >
            { submitting ? "SUBMITTING BILL..." : "SUBMIT CDM BILL →" }
          </button>
        </div>
      </div>

      {/* PREVIEW MODAL */}
      {showPreview && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(4px)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          boxSizing: "border-box"
        }}>
          <div style={{
            background: "#fff",
            borderRadius: 20,
            width: "100%",
            maxWidth: 450,
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
            overflow: "hidden",
            fontFamily: "'Inter', sans-serif"
          }}>
            {/* Modal Header */}
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #eee", background: "#f8f9fa" }}>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#1f2937" }}>Confirm CDM Order</h3>
              <p style={{ margin: "4px 0 0 0", fontSize: 14, color: "#6b7280", fontWeight: 600 }}>
                Hawker: <span style={{ color: "#673ab7", fontWeight: 800 }}>{selectedHawker?.name || selectedHawker?.partyId}</span>
              </p>
            </div>

            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", borderBottom: "2px solid #f3f4f6" }}>
                    <th style={{ paddingBottom: 8, fontSize: 12, fontWeight: 700, color: "#9ca3af" }}>PRODUCT</th>
                    <th style={{ paddingBottom: 8, fontSize: 12, fontWeight: 700, color: "#9ca3af", textAlign: "right" }}>QTY</th>
                  </tr>
                </thead>
                <tbody>
                  {cdmProducts.map(p => {
                    const key = p.id;
                    const qty = Number(items[key] || 0);
                    if (qty <= 0) return null;
                    return (
                      <tr key={p.id || p.msku} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "12px 0", fontSize: 14, fontWeight: 700, color: "#374151" }}>
                          {p.productName}
                          <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: "normal", marginTop: 2 }}>{p.msku}</div>
                        </td>
                        <td style={{ padding: "12px 0", fontSize: 16, fontWeight: 900, color: "#111827", textAlign: "right" }}>
                          {qty} CFC
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: "20px 24px", borderTop: "1px solid #eee", background: "#f8f9fa" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, fontSize: 16, fontWeight: 800 }}>
                <span style={{ color: "#4b5563" }}>Total Quantity</span>
                <span style={{ fontSize: 20, color: "#673ab7" }}>{totals.totalQty} CFC</span>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={() => setShowPreview(false)}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    padding: "14px 20px",
                    fontSize: 16,
                    fontWeight: 800,
                    background: "#f3f4f6",
                    color: "#4b5563",
                    border: "none",
                    borderRadius: 12,
                    cursor: "pointer"
                  }}
                >
                  Back
                </button>
                <button
                  onClick={async () => {
                    await submitBilling();
                    setShowPreview(false);
                  }}
                  disabled={submitting}
                  style={{
                    flex: 2,
                    padding: "14px 20px",
                    fontSize: 16,
                    fontWeight: 800,
                    background: "#673ab7",
                    color: "#fff",
                    border: "none",
                    borderRadius: 12,
                    cursor: "pointer"
                  }}
                >
                  {submitting ? "SUBMITTING..." : "CONFIRM & SUBMIT"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </BlankLayout>
  );
}

export default CdmBilling;
